// typescript
import React from "react";
import { Localizer } from "@/main/react/Types";
import { ThoughtInConversationDTO } from "@/generated-client";
import ThoughtDisplayerInConversation from "@/main/react/thoughts/ThoughtDisplayerInConversation";
import InsertedItemMotion from "@/main/react/component/InsertedItemMotion";

export type Props = {
    conversationPublicId: string;
    loggedInUsername?: string;
    argumentsInConversation?: ThoughtInConversationDTO[];
    agreementAgreementTitle?: string;
    disagreementAgreementTitle?: string;
    generalModelWrapper?: any;
    localizer: Localizer;
    isSensitive?: boolean;
    suppressSpoilerGuards?: boolean;
    onLoadMore?: () => void;
    loading?: boolean;
    hasMore?: boolean;
    isUserLoggedIn: boolean;
    highlightedArgumentPublicId?: string;
    openingUsername?: string;
    reactingUsername?: string;
    shouldInferParticipantsFromArgumentOrder?: boolean;
    newlyPublishedArgumentPublicIds?: string[];
};

export default function ThoughtInConversationListDisplayer(props: Props) {
    const {
        conversationPublicId = "",
        loggedInUsername,
        argumentsInConversation = [],
        agreementAgreementTitle = "",
        disagreementAgreementTitle = "",
        isUserLoggedIn = false,
        localizer,
        isSensitive = false,
        suppressSpoilerGuards = false,
        onLoadMore,
        loading = false,
        hasMore = false,
        highlightedArgumentPublicId,
        openingUsername,
        reactingUsername,
        shouldInferParticipantsFromArgumentOrder = false,
        newlyPublishedArgumentPublicIds = [],
    } = props;

    const canUseParticipantUsernames = !shouldInferParticipantsFromArgumentOrder
        && Boolean(openingUsername?.trim())
        && Boolean(reactingUsername?.trim())
        && openingUsername !== reactingUsername;
    const newlyPublishedArgumentIdSet = React.useMemo(
        () => new Set((newlyPublishedArgumentPublicIds ?? []).filter(Boolean)),
        [newlyPublishedArgumentPublicIds],
    );

    return (
        <div className="argument-list-container ">
            {argumentsInConversation.length > 0 && argumentsInConversation.map((argItem, idx) => {
                const displayOrientation = canUseParticipantUsernames
                    ? argItem.username === openingUsername
                    : idx % 2 === 0;
                const argumentPublicId = argItem.publicId ?? `argument-index-${idx}`;
                const isNewlyPublished = newlyPublishedArgumentIdSet.has(argItem.publicId ?? "");

                return (
                    <InsertedItemMotion
                        key={argumentPublicId}
                        active={isNewlyPublished}
                        playwrightId={isNewlyPublished ? `inserted-conversation-thought-${argumentPublicId}` : undefined}
                    >
                        <ThoughtDisplayerInConversation
                            argument={argItem}
                            loggedInUsername={loggedInUsername}
                            conversationPublicId={conversationPublicId}
                            agreementAgreementTitle={agreementAgreementTitle}
                            disagreementAgreementTitle={disagreementAgreementTitle}
                            isUserLoggedIn={isUserLoggedIn}
                            localizer={localizer}
                            displayOrientation={displayOrientation}
                            isSensitive={isSensitive}
                            suppressSpoilerGuard={suppressSpoilerGuards}
                            isHighlighted={argItem.publicId === highlightedArgumentPublicId}
                        />
                    </InsertedItemMotion>
                );
            })}

            {hasMore && (
                <div className="load-more-container">
                    <button
                        className="btn load-more"
                        onClick={() => onLoadMore && onLoadMore()}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Load more"}
                    </button>
                </div>
            )}
        </div>
    );
}
